"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";
import { ToastTheme } from "@/utils/constants";
import { useParams } from "next/navigation";
import SafeLink from "../SafeLink";
import { Locale } from "@/models/Locale";
import { AuthFeatures } from "./AuthFeatures";
import { goApiFetch } from "@/lib/goApiClient";

type ForgotPasswordFormData = {
  email: string;
};

type ApiError = {
  error: string;
};

export function ForgotPasswordForm() {
  const t = useTranslations("components.forgot_password");
  const { locale } = useParams<{ locale: Locale }>();

  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: "",
  });

  const [errors, setErrors] = useState({
    email: "",
    general: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (data: ForgotPasswordFormData = formData): boolean => {
    const newErrors = {
      email: "",
      general: "",
    };

    const trimmedEmail = data.email.trim();

    if (!trimmedEmail) {
      newErrors.email = t("error_email_required");
    } else if (!validateEmail(trimmedEmail)) {
      newErrors.email = t("error_invalid_email");
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleInputChange = (
    field: keyof ForgotPasswordFormData,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const sanitizedEmail = formData.email.trim();
    const sanitizedData: ForgotPasswordFormData = {
      email: sanitizedEmail,
    };
    setFormData(sanitizedData);

    if (!validateForm(sanitizedData)) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await goApiFetch("/v1/auth/forgot-password", {
        method: "POST",
        body: {
          email: sanitizedEmail.toLowerCase(),
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorData = data as ApiError;
        setErrors((prev) => ({
          ...prev,
          general: errorData.error || t("request_failed"),
        }));
        toast.error(errorData.error || t("request_failed"));
        return;
      }

      setIsSuccess(true);
      toast.success(t("email_sent_success"));
    } catch (error) {
      console.error("Forgot password error:", error);
      const errorMessage = t("network_error");
      setErrors((prev) => ({
        ...prev,
        general: errorMessage,
      }));
      toast.error(errorMessage, ToastTheme);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex w-full grow flex-col items-center justify-center gap-8 rounded-lg p-8">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-ht-brown mb-2 text-2xl font-semibold">
            {t("email_sent_title")}
          </h2>
          <p className="mb-4 text-gray-600">{t("email_sent_description")}</p>
          <p className="text-sm text-gray-500">{t("check_spam_folder")}</p>
        </div>

        <div className="flex flex-col gap-4">
          <SafeLink
            href="/sign-in"
            locale={locale}
            className="focus:shadow-outline bg-h-500 hover:bg-h-600 w-full max-w-44 cursor-pointer rounded-md px-6 py-3 text-center font-bold text-white transition-colors focus:outline-none"
          >
            {t("back_to_signin")}
          </SafeLink>

          <button
            onClick={() => {
              setIsSuccess(false);
              setFormData({ email: "" });
              setErrors({ email: "", general: "" });
            }}
            className="text-h-500 hover:text-h-600 text-sm underline"
          >
            {t("send_another_email")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex w-full grow flex-col items-start justify-center gap-8 rounded-lg p-8"
      aria-label="Forgot Password Form"
    >
      {/* Headers */}
      <div className="w-full">
        <h2 className="text-ht-brown mb-2 text-3xl font-semibold">
          {t("title")}
        </h2>
        <p className="text-gray-600">{t("description")}</p>
        <SafeLink
          href="/sign-in"
          locale={locale}
          className="text-h-400 mt-2 inline-block text-sm underline"
        >
          {t("back_to_signin")}
        </SafeLink>
        <AuthFeatures />
      </div>

      {/* General Error */}
      {errors.general && (
        <div className="w-full rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{errors.general}</p>
        </div>
      )}

      {/* Email Input */}
      <div className="w-full">
        <label
          htmlFor="email"
          className="block font-semibold text-gray-600"
          aria-label="Email Address"
        >
          {t("label_email")}
        </label>
        <input
          type="email"
          id="email"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          onBlur={() => handleInputChange("email", formData.email.trim())}
          required
          placeholder={t("placeholder_email")}
          className={`w-full rounded border-b border-b-[#E6997B] py-2 leading-tight focus:outline-none ${
            errors.email ? "border border-red-500" : ""
          } text-ht-brown text-sm font-semibold placeholder-gray-400`}
        />
        {errors.email && (
          <p className="mt-1 text-xs text-red-500 italic">{errors.email}</p>
        )}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="focus:shadow-outline bg-h-500 hover:bg-h-600 disabled:bg-h-300 w-full max-w-84 cursor-pointer rounded-md px-6 py-3 font-bold text-white transition-colors focus:outline-none disabled:cursor-not-allowed"
        aria-label="Send Reset Link"
      >
        {isLoading ? t("sending") : t("send_reset_link")}
      </button>
    </form>
  );
}
